import { Box } from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { useBookingsForOffer, useCreateBookingMutation } from "../../../services/bookingsService";
import { SnackbarError } from "../../base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../base/SnackbarAlert/SnackbarSuccess";
import { AddReservationDialog } from "../../dialog/AddReservationDialog/AddReservationDialog";
import { BookingReadonlyDisplay, CreateBookingForm } from "./CreateBookingForm";

export const CreateBookingsForOffer = (props) => {
    const { offer, readOnly = false } = props;
    const { t } = useTranslation();
    const { data: bookings, refetch } = useBookingsForOffer(offer.id);
    const { openDialog } = useDialogBetter();

    const { mutate: createBooking } = useCreateBookingMutation({
        onSuccess: () => {
            toast.custom(
                (curToast) => (
                    <SnackbarSuccess message={t("CreateBookingsForOffer.bookingCreated")} />
                ),
                {
                    position: "bottom-right",
                },
            );
            refetch();
        },
        onError: (error) => {
            toast.custom(
                (curToast) => (
                    <SnackbarError message={t("CreateBookingsForOffer.bookingCreationFailed")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const handleRecreateBooking = (booking) => {
        openDialog({
            Component: AddReservationDialog,
            componentProps: {
                initialCustomerIds: booking.customers.map((c) => c.customer.id),
                initialProductIds: booking.products.map((p) => p.id),
                offerId: offer.id,
                onSuccess: refetch,
            },
        });
    };

    const apartments = offer.products.map((op) => op.product).filter((p) => p.is_apartment);
    const apartmentsWithNoBookings = apartments
        .filter((ap) => !bookings?.results.some((b) => b.products.some((p) => p.id === ap.id)))
        .sort((a, b) => a.id.localeCompare(b.id)); // Ensure consistent order
    const apartmentChildren = Object.fromEntries(
        apartments.map((apartment) => [
            apartment.id,
            offer.products
                .map((op) => op.product)
                .filter((p) => p.parent_product?.id === apartment.id),
        ]),
    );
    const orphans = offer.products
        .map((op) => op.product)
        .filter((p) => !p.parent_product && !p.is_apartment);

    // At first assign all orphans to the first apartment with no bookings
    const [apartmentToOrphanMapping, setApartmentToOrphanMapping] = useState(
        apartmentsWithNoBookings.length > 0
            ? {
                  [apartmentsWithNoBookings[0].id]: orphans,
                  ...apartmentsWithNoBookings.slice(1).reduce((acc, ap) => {
                      acc[ap.id] = [];
                      return acc;
                  }, {}),
              }
            : {},
    );

    const handleMoveOrphanUp = (orphan) => {
        const currentApartmentId = Object.entries(apartmentToOrphanMapping).find(([_, orphans]) =>
            orphans.some((o) => o.id === orphan.id),
        )[0];
        const currentApartmentIdx = apartmentsWithNoBookings.findIndex(
            (ap) => ap.id === currentApartmentId,
        );

        if (currentApartmentIdx === -1 || currentApartmentIdx === 0) {
            // Orphan is already at the top or not found
            return;
        }

        const newApartmentIdx = currentApartmentIdx - 1;
        const newApartmentId = apartmentsWithNoBookings[newApartmentIdx].id;

        const currentApartmentOrphans = apartmentToOrphanMapping[currentApartmentId];
        const newApartmentOrphans = apartmentToOrphanMapping[newApartmentId];

        setApartmentToOrphanMapping((prevMapping) => ({
            ...prevMapping,
            [currentApartmentId]: currentApartmentOrphans.filter((p) => p.id !== orphan.id),
            [newApartmentId]: [...newApartmentOrphans, orphan],
        }));
    };

    const handleMoveOrphanDown = (orphan) => {
        const currentApartmentId = Object.entries(apartmentToOrphanMapping).find(([_, orphans]) =>
            orphans.some((o) => o.id === orphan.id),
        )[0];

        const currentApartmentIdx = apartmentsWithNoBookings.findIndex(
            (ap) => ap.id === currentApartmentId,
        );

        if (
            currentApartmentIdx === -1 ||
            currentApartmentIdx === apartmentsWithNoBookings.length - 1
        ) {
            // Orphan is already at the bottom or not found
            return;
        }

        const newApartmentIdx = currentApartmentIdx + 1;
        const newApartmentId = apartmentsWithNoBookings[newApartmentIdx].id;

        const currentApartmentOrphans = apartmentToOrphanMapping[currentApartmentId];
        const newApartmentOrphans = apartmentToOrphanMapping[newApartmentId];

        setApartmentToOrphanMapping((prevMapping) => ({
            ...prevMapping,
            [currentApartmentId]: currentApartmentOrphans.filter((p) => p.id !== orphan.id),
            [newApartmentId]: [...newApartmentOrphans, orphan],
        }));
    };

    const handleSubmit = (bookingData) => {
        const productsWithDiscounts = offer.products
            .filter((op) => bookingData.productIds.includes(op.product.id))
            .map((op) => ({
                product_id: op.product.id,
                discount_percent: Number(op.discount_percent),
            }));

        const requestBody = {
            customer_ids: offer.customers.map((c) => c.customer.id),
            start: bookingData.startDate,
            end: bookingData.endDate,
            note: bookingData.note,
            products: productsWithDiscounts,
            offer_id: offer.id,
        };
        createBooking(requestBody);
    };

    return (
        <Box>
            {bookings?.results.map((booking) => (
                <BookingReadonlyDisplay key={booking.id} booking={booking} readOnly={readOnly} />
            ))}
            {!readOnly &&
                apartmentsWithNoBookings.map((apartment, idx) => {
                    return (
                        <CreateBookingForm
                            key={apartment.id}
                            apartment={apartment}
                            apartmentChildProducts={apartmentChildren[apartment.id]}
                            extraChildProducts={apartmentToOrphanMapping[apartment.id]}
                            onMoveChildProductDown={handleMoveOrphanDown}
                            onMoveChildProductUp={handleMoveOrphanUp}
                            onSubmit={handleSubmit}
                            isFirst={idx === 0}
                            isLast={idx === apartmentsWithNoBookings.length - 1}
                        />
                    );
                })}
        </Box>
    );
};
